import { CutOffTime, DisbursementSummary, RedeemTransactionDetail } from "../types/redemption"

export { toCurrencyFormat, truncateAddress, formatDate, capitalizeText, removeUnderscore } from '@jagad/utils'

type RoundingMode = 'up' | 'down' | 'nearest' | 'none';

/**
 * Formats a number with European-style thousand separators and decimal notation.
 * Uses dots (.) as thousand separators and comma (,) as decimal separator.
 * 
 * @param input - The number or string to format
 * @param decimalPlaces - The number of decimal places to display (ignored when rounding is 'none')
 * @param rounding - The rounding mode to use when truncating decimal places
 *   - 'up': Always rounds up if there are excess decimal digits
 *   - 'down': Truncates (rounds down) excess decimal digits
 *   - 'nearest': Standard rounding - rounds up if next digit >= 5
 *   - 'none': No rounding, shows all available decimal digits
 * @returns The formatted number string with European notation
 * 
 * @example
 * ```typescript
 * toCurrencyFormatWithDecimal(1000.00001, 4) // Returns "1.000,0001"
 * toCurrencyFormatWithDecimal("0,11111", 4, 'none') // Returns "0,11111"
 * toCurrencyFormatWithDecimal(1234567.1236, 3, 'down') // Returns "1.234.567,123"
 * ```
 */
export function toCurrencyFormatWithDecimal(input: number | string, decimalPlaces: number, rounding: RoundingMode = 'up'): string {
  // Handle zero case
  if (input === 0 || input === "0") {
    return "0";
  }
  
  // Normalize input to work with standard decimal point format
  let normalizedInput: string;
  if (typeof input === 'number') {
    normalizedInput = input.toString();
  } else {
    // Convert European format (comma decimal) to standard format (dot decimal) for processing
    normalizedInput = input.replace(',', '.');
  }
  
  // Split into integer and decimal parts
  const parts = normalizedInput.split('.');
  const integerPart = parts[0] || '0';
  const originalDecimalPart = parts[1] || '';
  
  // If no decimal part exists, just return formatted integer
  if (originalDecimalPart === '') {
    return addThousandSeparators(integerPart);
  }
  
  // Handle decimal part based on rounding mode
  let finalDecimalPart: string;
  
  if (rounding === 'none') {
    // No rounding - use all original decimal digits
    finalDecimalPart = originalDecimalPart;
  } else {
    // Apply rounding logic
    if (originalDecimalPart.length <= decimalPlaces) {
      // Pad with zeros if needed
      finalDecimalPart = originalDecimalPart.padEnd(decimalPlaces, '0');
    } else {
      // Need to round
      const truncatedPart = originalDecimalPart.substring(0, decimalPlaces);
      const nextDigit = parseInt(originalDecimalPart.charAt(decimalPlaces) || '0');
      
      if (rounding === 'down') {
        finalDecimalPart = truncatedPart;
      } else if (rounding === 'up' && nextDigit > 0) {
        finalDecimalPart = roundUpDecimal(truncatedPart);
      } else if (rounding === 'nearest' && nextDigit >= 5) {
        finalDecimalPart = roundUpDecimal(truncatedPart);
      } else {
        finalDecimalPart = truncatedPart;
      }
    }
  }
  
  // Combine integer and decimal parts with European formatting
  const formattedInteger = addThousandSeparators(integerPart);
  return `${formattedInteger},${finalDecimalPart}`;
}

// Helper function to add thousand separators (dots) to integer part
function addThousandSeparators(integerStr: string): string {
  return integerStr.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

// Helper function to round up a decimal string
function roundUpDecimal(decimalStr: string): string {
  const digits = decimalStr.split('').map(d => parseInt(d));
  let carry = 1;
  
  // Start from rightmost digit
  for (let i = digits.length - 1; i >= 0 && carry; i--) {
    digits[i] += carry;
    if (digits[i] === 10) {
      digits[i] = 0;
      carry = 1;
    } else {
      carry = 0;
    }
  }
  
  // If still have carry, the result would overflow (e.g., 999 -> 1000)
  // In this case, we return all 0s and let the integer part handle the overflow
  if (carry) {
    return '0'.repeat(decimalStr.length);
  }
  
  return digits.join('');
}

/**
 * Formats a timestamp into a readable date string.
 *
 * This function accepts timestamps in various formats (string, number, or Date) and
 * returns a formatted string representing the date. Optionally, it can include the time.
 *
 * @param val - The input timestamp, which can be a string, number (Unix timestamp), or a Date object.
 * @param withTime - (Optional) If set to true, the resulting string will include the time. Defaults to false.
 *
 * @returns The formatted date string. For example, an input of '2022-11-29T03:40:40.000Z' would return '29 Nov 2022'.
 *
 * @example
 * ```typescript
 * formatDate('2022-11-29T03:40:40.000Z'); // Outputs: '29 Nov 2022'
 * formatDate('2022-11-29T03:40:40.000Z', true); // Outputs: '29 Nov 2022, 03:40:40'
 * ```
 */

/**
 * Groups an array of objects by a specified key.
 * some elements will be removed if they are duplicates
 * @param array array to be grouped
 * @param key key to group by
 * @returns grouped array
 */
export const groupArrayBy = <T>(array: T[], key: keyof T) => {
  const grouped = []
  const group = new Set<T[keyof T]>()
  for (let el of array) {
    if (!group.has(el[key])) {
      group.add(el[key])
      grouped.push(el)
    }
  }
  return grouped
}
  
/**
 * Filters an array of objects by a specified key and returns the values of another key.
 *
 * @param array array to be filtered
 * @param key key to filter by
 * @param value value to match
 * @param extract key to extract values from
 * @returns array of values
 */
export const filterAndExtract = <T>(currencies: T[], key: keyof T, value: T[keyof T], extract: keyof T) => {
  return currencies
    .filter(item => item[key] === value)
    .map(item => item[extract])
}

/**
 * Filters an array of objects based on whether specified properties contain the search query.
 * Case-insensitive search.
 * 
 * @param items - The array of items to filter
 * @param searchQuery - The search query string
 * @param properties - Single property key or array of property keys to search within
 * @returns Filtered array of items that match the search query
 * 
 * @example
 * ```typescript
 * // Search by a single property
 * const filteredByName = filterBySearchQuery(addresses, 'john', 'name');
 * 
 * // Search across multiple properties
 * const filteredByMultiple = filterBySearchQuery(options, 'query', ['name', 'accountNumber', 'nickname']);
 * ```
 */
export const filterBySearchQuery = <T>(
  items: T[],
  searchQuery: string,
  properties: keyof T | Array<keyof T>
): T[] => {
  if (!searchQuery) return items;
  
  const searchProperties = Array.isArray(properties) ? properties : [properties];
  const lowercaseQuery = searchQuery.toLowerCase();
  
  return items.filter((item) => {
    return searchProperties.some(property => {
      const value = item[property];
      if (typeof value === 'string') {
        return value.toLowerCase().includes(lowercaseQuery);
      } else if (value === null || value === undefined) {
        return false;
      } else {
        // Handle non-string values by converting to string
        return String(value).toLowerCase().includes(lowercaseQuery);
      }
    });
  });
};


/**
 * Re-maps a number from one range to another.
 *
 * This function takes a number `value` and maps it from a source range defined by `sourceMin` and `sourceMax`
 * to a target range defined by `targetMin` and `targetMax`. The value is first clamped to the source range.
 * This function mimics the behavior of the `map()` function in p5.js.
 *
 * @param value - The number to be re-mapped.
 * @param sourceMin - The lower bound of the value's current range.
 * @param sourceMax - The upper bound of the value's current range.
 * @param targetMin - The lower bound of the value's target range.
 * @param targetMax - The upper bound of the value's target range.
 * @returns The re-mapped number, now within the target range.
 *
 * @example
 * // Maps 50 from the range 0-100 to the range 500-1000
 * const result = mapValue(50, 0, 100, 500, 1000);
 * console.log(result); // Output: 750
 */
export function mapValue(
  value: number,
  sourceMin: number,
  sourceMax: number,
  targetMin: number,
  targetMax: number,
): number {
  // Clamps the value within the source range.
  const clampedValue = Math.min(
    Math.max(value, Math.min(sourceMin, sourceMax)),
    Math.max(sourceMin, sourceMax),
  )

  // Calculates the ratio of the clamped value within the source range.
  const ratio = (clampedValue - sourceMin) / (sourceMax - sourceMin)

  // Applies the ratio to the target range.
  return targetMin + ratio * (targetMax - targetMin)
}

/**
 * Detects if the user is on iOS or Android and redirects to a Telegram customer service chatbot
 * @param botUsername The username of the Telegram bot (without the @ symbol)
 */
export function redirectToTelegramSupport(
  botUsername: string,
): void {
  // Check if Telegram WebApp is available
  const telegram = window.Telegram?.WebApp;
  
  if (telegram) {
    telegram.openTelegramLink(`https://t.me/${botUsername}`);
  } else {
    window.location.href = `https://t.me/${botUsername}`;
  }
}

export function lowerCaseFirstWord(input: string): string {
  if (!input) {
      return ''
  }
  return input.charAt(0).toLowerCase() + input.slice(1)
}

export function joinArrayOfStringIntoString(arr: string[]): string {
  if (arr.length === 0) {
      return ''
  }
  if (arr.length === 1) {
      return arr[0]
  }
  if (arr.length === 2) {
      return `${arr[0]} and ${arr[1]}`
  }

  const lastElement = arr.pop()
  return `${arr.join(', ')}, and ${lastElement}`
}


/**
 * Calculates the number of disbursements needed to reach the requested amount,
 * rounded up to the nearest integer.
 * @param requestedAmount 
 * @param maximumAmountPerDisbursement 
 * @returns 
 */
export const getSplitDisbursementCount = (
  requestedAmount: number,
  maximumAmountPerDisbursement: number
) => {
  return Math.ceil(requestedAmount / maximumAmountPerDisbursement)
}

/**
 * Checks if the current time in WIB (Indonesian Western Time, UTC+7) is within a specified cut-off time range.
 * The function handles cut-off times spanning across midnight and time formats with or without leading zeros.
 *
 * @param cutOffTime An object containing the start and end times of the cut-off period, formatted as "HH:MM" or "H:MM".
 * @returns {boolean} True if the current time is within the specified cut-off time range; otherwise, false.
 *
 * @example
 * const cutOff = { start: "22:00", end: "2:00" };
 * console.log(isWithinCutOffTime(cutOff)); // Outputs: true if current time in WIB is between 22:00 and 02:00, false otherwise.
 */
export const isWithinCutOffTime = (
  cutOffTime?: CutOffTime
): boolean => {
  if (!cutOffTime) {
    return false
  }

  const normalizeTime = (time: string): string => {
    const [hours, minutes] = time.split(':').map(val => val.padStart(2, '0'))
    return `${hours}:${minutes}`
  }

  const currentTimeInWIB = getCurrentTimeInWIB()
  const normalizedCurrentTime = normalizeTime(currentTimeInWIB)
  const normalizedStartTime = normalizeTime(cutOffTime.start)
  const normalizedEndTime = normalizeTime(cutOffTime.end)

  // Checking if the cut-off time spans across midnight
  if (normalizedStartTime > normalizedEndTime) {
    // If current time is after start time or before end time, it's within the range.
    return (
      normalizedCurrentTime >= normalizedStartTime ||
      normalizedCurrentTime <= normalizedEndTime
    )
  } else {
    // For same-day cut-off times, check if current time is between start and end times.
    return (
      normalizedCurrentTime >= normalizedStartTime &&
      normalizedCurrentTime <= normalizedEndTime
    )
  }
}

/**
* Gets the current time formatted as "HH:MM" in Indonesian Western Time (WIB, UTC+7).
* This function calculates the current time in WIB by first converting the local time to UTC,
* then applying the WIB offset, and finally formatting the result into a human-readable string.
*
* @returns {string} The current time in WIB, formatted as "HH:MM".
*/
export const getCurrentTimeInWIB = (): string => {
  const now = new Date()
  const localOffset = now.getTimezoneOffset() // Local timezone offset in minutes
  const wibOffset = -420 // WIB is UTC+7, timezone offset in minutes is negative for east of GMT

  // If the local timezone is already WIB, use the local time; otherwise, convert to WIB
  const wibTime =
    localOffset === wibOffset
      ? now
      : new Date(now.getTime() + (localOffset - wibOffset) * 60000)

  // Format to HH:MM
  return (
    wibTime.getHours().toString().padStart(2, '0') +
    ':' +
    wibTime.getMinutes().toString().padStart(2, '0')
  )
}

type Mapping = { [key: string]: any }
type TransactionType = 'old' | 'new'
type TransactionCode = number | string

/**
 * Extracts data from a transaction object based on the specified type and transaction code using field mappings.
 * It handles null values by not overwriting existing non-null fields with null values.
 *
 * @param {any} transaction - The transaction object from which to extract the data.
 * @param {TransactionType} type - The type of the transaction ('old' or 'new') to determine the field mappings.
 * @param {TransactionCode} code - The transaction code to specify which fields to extract.
 * @param {Mapping} mapping - The mapping object defining the path of fields in the transaction object.
 * @returns {any} An object containing the extracted data based on the field mappings for the specified type and code.
 * @example
 * const transactionData = { /*...*\/ };
 * const extractedData = extractData(transactionData, 'old', 201, fieldMapping);
 */
export function extractData(
  transaction: any,
  type: TransactionType,
  code: TransactionCode,
  mapping: Mapping,
): any {
  const typeMapping = mapping[type]
  if (!typeMapping) {
    return {}
  }

  const codeMapping = typeMapping[code]
  if (!codeMapping) {
    return {}
  }

  const extracted: any = {}
  codeMapping.forEach((field: any) => {
    const fieldName = Object.keys(field)[0]
    const fieldPath = field[fieldName]
    const fieldValue = getValueFromPath(transaction, fieldPath)

    // Check if the field is already set to a non-null value
    if (extracted[fieldName] == null) {
      extracted[fieldName] = fieldValue
    }
  })

  return extracted
}

/**
 * Retrieves a value from a nested object based on a specified path.
 * The path is represented as a string where each segment is separated by a '.'.
 *
 * @param {any} dataObject - The object from which to extract the value.
 * @param {string} pathString - The path to the desired value within the object.
 * @returns {any} The value found at the specified path within the object, or `undefined` if not found.
 * @example
 * const obj = {
 *   a: { b: { c: 5 } }
 * };
 * const value = getValueFromPath(obj, "a.b.c"); // Returns 5
 */
function getValueFromPath(dataObject: any, pathString: string): any {
  return pathString.split('.').reduce((currentData, currentPathSegment) => {
    return currentData && currentData[currentPathSegment]
  }, dataObject)
}

export function getDisbursementSummary(
  redeemTransaction: Partial<RedeemTransactionDetail>,
): DisbursementSummary {
  const result: DisbursementSummary = {
    requested: redeemTransaction.redeem_request?.ui_amount || 'N/A',
    disbursed: redeemTransaction.disbursement_amount?.disbursed_amount || 'N/A',
    inProgress:
      redeemTransaction.disbursement_amount?.in_progress_amount || 'N/A',
    returned: redeemTransaction.disbursement_amount?.returned_amount || 'N/A',
    disbursedCount: redeemTransaction.total_disbursed_item || 'N/A',
    totalCount: redeemTransaction.total_item || 'N/A',
    isMultipart: redeemTransaction.is_multipart || false,
  }

  return result
}

/**
 * Compares two version strings (e.g., '1.2.3' and '1.3.0')
 * @param v1 First version string to compare
 * @param v2 Second version string to compare
 * @returns 1 if v1 > v2, -1 if v1 < v2, 0 if equal
 */
export function versionCompare(v1: string | undefined, v2: string | undefined): number {
  // Handle undefined or non-string inputs
  const version1: string = typeof v1 === 'string' ? v1 : '';
  const version2: string = typeof v2 === 'string' ? v2 : '';
  
  // Split version strings into arrays of version parts
  const v1Parts: string[] = version1.replace(/^\s+|\s+$/g, '').split('.');
  const v2Parts: string[] = version2.replace(/^\s+|\s+$/g, '').split('.');
  
  // Get the maximum length of both version arrays
  const maxLength: number = Math.max(v1Parts.length, v2Parts.length);
  
  // Compare each part of the version
  for (let i = 0; i < maxLength; i++) {
    // Convert parts to integers, defaulting to 0 if not present
    const p1: number = parseInt(v1Parts[i]) || 0;
    const p2: number = parseInt(v2Parts[i]) || 0;
    
    // If parts are equal, continue to next part
    if (p1 === p2) continue;
    
    // Return result based on comparison
    return p1 > p2 ? 1 : -1;
  }
  
  // Versions are equal
  return 0;
}

/**
 * Delays the execution of the next line of code for the specified number of milliseconds.
 * @param ms The number of milliseconds to delay.
 * @returns A Promise that resolves after the specified number of milliseconds.
 */
export function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Checks if the current environment is a build/CI environment like Netlify's HeadlessChrome
 */
export function isBuildEnvironment(): boolean {
  return typeof navigator !== 'undefined' && 
      (/HeadlessChrome|netlify|CI|build|test/i.test(navigator.userAgent || '') ||
      // Also check for common CI environment variables
      typeof process !== 'undefined' && 
      (process.env?.CI === 'true' || process.env?.NETLIFY === 'true'));
}

/**
 * A function to round a float into having a specific decimal places
 * When decimal place is not provided the function will check based on amount provided
 * if amount is greater than 1, the decimal places will be up to 2 digits
 * if amount is less than 1, the decimal places will be up to 4 digits
 * @param amount
 * @param decimalPlace
 * @param round
 * @returns
 */
export function roundAmount(
  amount: number,
  round: 'up' | 'down',
  decimalPlace?: number,
) {
  const appliedDecimal = decimalPlace ? decimalPlace : amount > 1 ? 2 : 4
  if (round === 'up') {
    return Math.ceil(amount * 10 ** appliedDecimal) / 10 ** appliedDecimal
  } else {
    return Math.floor(amount * 10 ** appliedDecimal) / 10 ** appliedDecimal
  }
}

export function convertDecimalFormat(value: string): string {
  if (value.includes(',')) {
    return value.replace(',', '.');
  }
  return value;
}

/**
 * Decodes HTML entities in a string back to their original characters.
 * This function reverses the encoding done by the backend's sanitizeText function.
 *
 * @param text - The HTML-encoded text to decode
 * @returns The decoded text with HTML entities converted back to characters
 *
 * @example
 * ```typescript
 * decodeHtmlEntities("Don&#x27;t lose access") // Returns: "Don't lose access"
 * decodeHtmlEntities("A &amp; B") // Returns: "A & B"
 * ```
 */
export function decodeHtmlEntities(text: string): string {
  if (!text) return text

  return text
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/')
    .replace(/&quot;/g, '"')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&') // This must be last to avoid double-decoding
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}